import { FormStep, AppraisalData } from './types';
import { STEP_SCHEMAS } from './validation-schemas';
import { lazy } from 'react';

// Lazy load step components
const TrimConfirmationStep = lazy(() => import('../components/form-steps/TrimConfirmationStep').then(m => ({ default: m.TrimConfirmationStep })));
const MileageStep = lazy(() => import('../components/form-steps/MileageStep').then(m => ({ default: m.MileageStep })));
const ModificationsStep = lazy(() => import('../components/form-steps/ModificationsStep').then(m => ({ default: m.ModificationsStep })));
const OwnershipStep = lazy(() => import('../components/form-steps/OwnershipStep').then(m => ({ default: m.OwnershipStep })));
const KeysStep = lazy(() => import('../components/form-steps/KeysStep').then(m => ({ default: m.KeysStep })));
const AccidentsStep = lazy(() => import('../components/form-steps/AccidentsStep').then(m => ({ default: m.AccidentsStep })));
const ConditionStep = lazy(() => import('../components/form-steps/ConditionStep').then(m => ({ default: m.ConditionStep })));
const StartsRunsDrivesStep = lazy(() => import('../components/form-steps/StartsRunsDrivesStep').then(m => ({ default: m.StartsRunsDrivesStep })));
const PreferenceStep = lazy(() => import('../components/form-steps/PreferenceStep').then(m => ({ default: m.PreferenceStep })));
const CommentsStep = lazy(() => import('../components/form-steps/CommentsStep').then(m => ({ default: m.CommentsStep })));
const ContactStep = lazy(() => import('../components/form-steps/ContactStep').then(m => ({ default: m.ContactStep })));

/**
 * Configurable form steps - Easy to add/remove questions
 * To add a new step:
 * 1. Create the step component
 * 2. Add validation schema to validation-schemas.ts
 * 3. Add step configuration here
 */
export const FORM_STEPS: FormStep[] = [
  {
    id: 'trimConfirmation',
    title: 'Is this the correct trim for your {model}?',
    subtitle: 'Please verify the trim level matches your vehicle',
    component: TrimConfirmationStep,
    validation: STEP_SCHEMAS.trimConfirmation,
    required: true,
    condition: (data: AppraisalData) => Boolean(data.trim && data.trim.trim().length > 0),
  },
  {
    id: 'mileage',
    title: 'How many miles are on your {model}?',
    subtitle: 'Please provide us with the current miles on your vehicle',
    component: MileageStep,
    validation: STEP_SCHEMAS.mileage,
    required: true,
  },
  {
    id: 'modifications',
    title: 'Does your {model} have any modifications?',
    subtitle: 'Aftermarket modifications or alterations',
    component: ModificationsStep,
    validation: STEP_SCHEMAS.modifications,
    required: true,
  },
  {
    id: 'ownership',
    title: 'Are you the original owner?',
    subtitle: 'Ownership history helps us understand your vehicle',
    component: OwnershipStep,
    validation: STEP_SCHEMAS.ownership,
    required: true,
  },
  {
    id: 'keys',
    title: 'How many keys do you have?',
    subtitle: 'Including key fobs and remotes',
    component: KeysStep,
    validation: STEP_SCHEMAS.keys,
    required: true,
  },
  {
    id: 'accidents',
    title: 'Has your {model} been in an accident?',
    component: AccidentsStep,
    validation: STEP_SCHEMAS.accidents,
    required: true,
  },
  {
    id: 'condition',
    title: 'What condition is it in?',
    subtitle: 'How would you describe your {model}?',
    component: ConditionStep,
    validation: STEP_SCHEMAS.condition,
    required: true,
  },
  {
    id: 'startsRunsDrives',
    title: 'Does your {model} start, run and drive?',
    subtitle: 'This helps us determine the best way to handle your appraisal',
    component: StartsRunsDrivesStep,
    validation: STEP_SCHEMAS.startsRunsDrives,
    required: true,
  },
  {
    id: 'preference',
    title: 'Would you prefer to sell or trade your {model}?',
    subtitle: 'Whether you choose to sell or trade, the value of your {model} will remain consistent.',
    component: PreferenceStep,
    validation: STEP_SCHEMAS.preference,
    required: true,
  },
  {
    id: 'comments',
    title: 'Anything else we should know about your {model}?',
    subtitle: 'Additional options or packages? Any issues or concerns? New tires? Broken, chipped, cracked glass? etc',
    component: CommentsStep,
    validation: STEP_SCHEMAS.comments,
    required: false,
  },
  {
    id: 'contact',
    title: 'Tell us where to send your offer!',
    component: ContactStep,
    validation: STEP_SCHEMAS.contact,
    required: true,
  },
];

/**
 * Get visible steps based on current form data
 * This allows for conditional step display
 */
export function getVisibleSteps(data: AppraisalData): FormStep[] {
  return FORM_STEPS.filter(step => {
    if (step.condition) {
      return step.condition(data);
    }
    return true;
  });
}

/**
 * Get step by ID
 */
export function getStepById(stepId: string): FormStep | undefined {
  return FORM_STEPS.find(step => step.id === stepId);
}

/**
 * Get step index in visible steps
 */
export function getStepIndex(stepId: string, visibleSteps: FormStep[]): number {
  return visibleSteps.findIndex(step => step.id === stepId);
}

/**
 * Get next step ID
 */
export function getNextStepId(currentStepId: string, visibleSteps: FormStep[]): string | null {
  const currentIndex = getStepIndex(currentStepId, visibleSteps);
  const nextStep = visibleSteps[currentIndex + 1];
  return nextStep ? nextStep.id : null;
}

/**
 * Get previous step ID
 */
export function getPreviousStepId(currentStepId: string, visibleSteps: FormStep[]): string | null {
  const currentIndex = getStepIndex(currentStepId, visibleSteps);
  const previousStep = visibleSteps[currentIndex - 1];
  return previousStep ? previousStep.id : null;
}

/**
 * Check if step is complete
 */
export function isStepComplete(step: FormStep, data: AppraisalData): boolean {
  try {
    // Extract relevant data for this step
    const stepData = getStepData(step.id, data);
    step.validation.parse(stepData);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get data relevant to a specific step
 */
function getStepData(stepId: string, data: AppraisalData): any {
  switch (stepId) {
    case 'trimConfirmation':
      return { trim: data.trim };
    case 'mileage':
      return { mileage: data.mileage };
    case 'modifications':
      return {
        modifications: data.modifications,
        modificationsDescription: data.modificationsDescription,
      };
    case 'ownership':
      return { firstOwner: data.firstOwner };
    case 'keys':
      return { numberOfKeys: data.numberOfKeys };
    case 'accidents':
      return {
        accidents: data.accidents,
        accidentDetails: data.accidentDetails,
      };
    case 'condition':
      return { condition: data.condition };
    case 'startsRunsDrives':
      return { startsRunsDrives: data.startsRunsDrives };
    case 'preference':
      return { sellOrTrade: data.sellOrTrade };
    case 'comments':
      return { comments: data.comments };
    case 'contact':
      return {
        name: data.name,
        email: data.email,
        phone: data.phone,
        postalCode: data.postalCode,
        consentToPromotions: data.consentToPromotions,
      };
    default:
      return {};
  }
}

/**
 * Calculate form progress percentage
 */
export function calculateProgress(currentStepId: string, visibleSteps: FormStep[]): number {
  const currentIndex = getStepIndex(currentStepId, visibleSteps);
  return Math.round(((currentIndex + 1) / visibleSteps.length) * 100);
}

/**
 * Get form completion status
 */
export function getFormCompletionStatus(data: AppraisalData): {
  totalSteps: number;
  completedSteps: number;
  percentage: number;
  isComplete: boolean;
} {
  const visibleSteps = getVisibleSteps(data);
  const completedSteps = visibleSteps.filter(step => isStepComplete(step, data)).length;
  const totalSteps = visibleSteps.length;
  const percentage = Math.round((completedSteps / totalSteps) * 100);
  const isComplete = completedSteps === totalSteps;

  return {
    totalSteps,
    completedSteps,
    percentage,
    isComplete,
  };
}